import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * CTASection component for the homepage
 * Call-to-action section to encourage visitors to get in touch
 * Redesigned with a split layout for better image display and readability
 * Supports multiple languages through i18n
 */
const CTASection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 1.2,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-24 bg-gradient-to-br from-primary to-primary/90 text-white relative overflow-hidden scroll-section"
    >
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Image Column - Displayed prominently */}
          <motion.div 
            className="w-full lg:w-1/2 order-2 lg:order-1"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.div 
              className="relative rounded-xl overflow-hidden shadow-2xl border-4 border-white/20"
              variants={imageVariants}
            >
              {/* Image with proper styling */}
              <img 
                src="/natux-banner.jpg" 
                alt="NatuX Translations" 
                className="w-full h-auto object-cover"
              />
              
              {/* Subtle overlay to enhance image visibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent"></div>
              
              {/* Decorative elements */}
              <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-primary/70 to-transparent"></div>
              <div className="absolute top-4 right-4 bg-accent/90 text-dark px-4 py-2 rounded-md text-sm font-medium uppercase tracking-wider">
                Premium Quality
              </div>
            </motion.div>
          </motion.div>
          
          {/* Content Column - Clean and readable */}
          <motion.div 
            className="w-full lg:w-1/2 order-1 lg:order-2"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="bg-white/10 backdrop-blur-sm p-8 md:p-12 rounded-xl border border-white/20 shadow-lg">
              <motion.div variants={itemVariants} className="w-20 h-1 bg-accent rounded-full mb-8"></motion.div>
              
              <motion.h2 
                variants={itemVariants}
                className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 tracking-wide"
              >
                {t('cta.title')}
              </motion.h2>
              
              <motion.p 
                variants={itemVariants}
                className="text-lg md:text-xl text-white font-light mb-8 leading-relaxed"
              >
                {t('cta.description')}
              </motion.p>
              
              <motion.div 
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link 
                  to="/contact" 
                  className="inline-block bg-accent text-dark px-8 py-4 rounded-lg uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-white hover:text-primary hover:translate-y-[-2px] shadow-lg"
                >
                  {t('cta.getInTouch')}
                </Link>
                <Link 
                  to="/services" 
                  className="inline-block bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:bg-white/20 hover:translate-y-[-2px] border border-white/30 shadow-lg"
                >
                  {t('cta.ourServices')}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
